import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "../modals/ResourceViewerModal.module.css";
import localStyles from "./DocumentReferenceCardExtended.module.css";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";
import PDFViewer from "../utils/PDFViewer";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchEncounterByReference } from "../services/encounterService";
import AttachmentHandler from "../components/AttachmentHandler";
import { resolveName } from "../utils/messageUtils";
import { Field, SectionHeader, renderReference, renderCodeable } from "../utils/renderUtils";

/* ----------------------------------------------------------
   Helpers
---------------------------------------------------------- */
/**
 * Normalize Media.content to our common attachment shape.
 */
const normalizeAttachmentsFromMedia = (media) => {
  if (!media) return [];

  const att = media.content;
  if (!att) return [];

  return [
    {
      title: att.title || media.note?.[0]?.text || "media-content",
      url: att.url || null,
      data: att.data || null,
      contentType: att.contentType || null,
      creation: media.createdDateTime || null,
      source: "Media.content",
      raw: att,
    },
  ];
};

/* ----------------------------------------------------------
   Main Component
---------------------------------------------------------- */

const MediaCardExtended = ({ resource, handleShowNested, fhirClient }) => {
  const store = useStore();
  const attachments = normalizeAttachmentsFromMedia(resource);
  const [patientData, setPatientData] = useState(null);
  const [provider, setProvider] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [encounter, setEncounter] = useState(null);

  const [loading, setLoading] = useState({
    patient: false,
    provider: false,
    org: false,
    encounter: false,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [modalType, setModalType] = useState("generic");
  const [modalTitle, setModalTitle] = useState("");

  /* ----------------------------------------------------------
     Fetch subject / operator / organization
  ---------------------------------------------------------- */

  useEffect(() => {
    setPatientData(null);
    setProvider(null);
    setOrganization(null);
    setEncounter(null);

    if (!resource || !fhirClient) return;

    /* PATIENT */
    const fetchPatient = async () => {
      if (!resource?.subject?.reference) return;
      try {
        setLoading((l) => ({ ...l, patient: true }));
        const p = await fetchPatientByReference(
          resource.subject.reference,
          fhirClient,
          store
        );
        setPatientData(p);
      } catch {
        setPatientData(null);
      } finally {
        setLoading((l) => ({ ...l, patient: false }));
      }
    };

    /* OPERATOR (Practitioner) */
    const fetchOperator = async () => {
      const ref = resource.operator?.reference;
      if (!ref || !ref.startsWith("Practitioner/")) return;

      try {
        setLoading((l) => ({ ...l, provider: true }));
        const prov = await fetchProviderByReference(ref, fhirClient, store);
        setProvider(prov);
      } catch {
        setProvider(null);
      } finally {
        setLoading((l) => ({ ...l, provider: false }));
      }
    };

    /* ORGANIZATION */
    const fetchOrg = async () => {
      const ref = resource?.device?.reference; // Media.device *might* be an Organization or Device
      if (!ref || !ref.startsWith("Organization/")) return;

      try {
        setLoading((l) => ({ ...l, org: true }));
        const org = await fetchOrganizationByReference(ref, fhirClient, store);
        setOrganization(org);
      } catch {
        setOrganization(null);
      } finally {
        setLoading((l) => ({ ...l, org: false }));
      }
    };

    /* ENCOUNTER */
    const fetchEncounterData = async () => {
      const ref = resource?.encounter?.reference;
      if (!ref) return;

      try {
        setLoading((l) => ({ ...l, encounter: true }));
        const enc = await fetchEncounterByReference(ref, fhirClient, store);
        setEncounter(enc);
      } catch {
        setEncounter(null);
      } finally {
        setLoading((l) => ({ ...l, encounter: false }));
      }
    };

    fetchPatient();
    fetchOperator();
    fetchOrg();
    fetchEncounterData();
  }, [resource, fhirClient, store]);

  /* ----------------------------------------------------------
     Derived info: TODO: refactor this
  ---------------------------------------------------------- */

  const patientNameObj = patientData?.name?.[0];
  const patientFullName = patientNameObj
    ? `${patientNameObj.given?.join(" ") || ""} ${
        patientNameObj.family || ""
      }`.trim()
    : "Unknown";

  const patientPhotoUrl =
    patientData?.photo?.[0]?.url ||
    (patientData?.gender === "female" ? FemalePatient : MalePatient);

  const providerNameObj = provider?.name?.[0];
  const providerFullName = providerNameObj
    ? `${providerNameObj.prefix?.join(" ") || ""} ${
        providerNameObj.given?.join(" ") || ""
      } ${providerNameObj.family || ""}`.trim()
    : "Unknown Provider";

  const providerPhotoUrl =
    provider?.photo?.[0]?.url ||
    (provider?.gender === "female" ? FemaleProvider : MaleProvider);

  const orgDisplayName =
    organization?.name ||
    (organization?.id ? `Organization/${organization.id}` : "Unknown Organization");

  const orgIcon = DefaultOrg;

  /* ----------------------------------------------------------
     Render
  ---------------------------------------------------------- */

  return (
    <div className="p-4 border rounded shadow-sm bg-white">

      {/* SUBJECT / OPERATOR HEADER */}
      <div className={localStyles.headerRow}>
        {/* Patient */}
        <div className={localStyles.entityBlock}>
          <button
            className={localStyles.entityImageButton}
            onClick={() => handleShowNested?.(patientData, patientFullName)}
          >
            <img src={patientPhotoUrl} alt="Patient" className={localStyles.avatar} />
          </button>
          <div>
            <Field label="Patient">{patientFullName}</Field>
            <Field label="Birth Date">{patientData?.birthDate}</Field>
          </div>
        </div>

        {/* Provider / Organization */}
        <div className={localStyles.organizationBlock}>
          <button
            className={localStyles.entityImageButton}
            onClick={() => handleShowNested?.(provider, providerFullName)}
          >
            <img
              src={providerPhotoUrl || DefaultProvider}
              alt="Provider"
              className={localStyles.avatar}
            />
          </button>

          <div className={localStyles.orgText} style={{ marginRight: "1rem" }}>
            <Field label="Operator">{providerFullName}</Field>
            <Field label="Active">
              {provider ? (provider.active ? "Yes" : "No") : "N/A"}
            </Field>
          </div>

          <button
            className={localStyles.entityImageButton}
            onClick={() => handleShowNested?.(organization, orgDisplayName)}
          >
            <img src={orgIcon} alt="Organization" className={localStyles.avatar} />
          </button>

          <div className={localStyles.orgText}>
            <div className={localStyles.orgName}>{orgDisplayName}</div>
            <div className={localStyles.orgType}>
              {organization?.type?.text ||
                organization?.type?.coding?.[0]?.display ||
                ""}
            </div>
          </div>
        </div>
      </div>

      {/* IDENTIFICATION */}
      <SectionHeader>Identification</SectionHeader>
      <div className={styles.indented}>
         <div className={styles.sectionContent}>
        <Field label="Media ID">{resource.id}</Field>
        <Field label="Status">{resource.status}</Field>
        <Field label="Modality">{renderCodeable(resource.modality)}</Field>
        <Field label="Type">{renderCodeable(resource.type)}</Field>
        <Field label="View">{renderCodeable(resource.view)}</Field>
        <Field label="Created">{resource.createdDateTime}</Field>
        <Field label="Duration">{resource.duration}</Field>
        <Field label="Encounter">
          {resource.encounter?.reference
            ? renderReference(
                resource.encounter.reference,
                resolveName(resource.encounter.reference, store) || encounter?.id,
                handleShowNested
              )
            : <span className="text-muted">N/A</span>
          }
        </Field>
        {Array.isArray(resource.identifier) && resource.identifier.length > 0 && (
          <Field label="Identifiers">
            {resource.identifier.map((id, i) => (
              <div key={i} className="ps-3">
                {id.system} — {id.value}
              </div>
            ))}
          </Field>
        )}
      </div></div>

      {/* CONTENT */}
      <SectionHeader>Content</SectionHeader>
      <div>
        <AttachmentHandler attachments={attachments} fhirClient={fhirClient} />
      </div>

      {/* MODAL */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          if (modalBlobUrl) URL.revokeObjectURL(modalBlobUrl);
          setModalBlobUrl("");
        }}
        size="xl"
        fullscreen="md-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {modalType === "pdf" && <PDFViewer blobUrl={modalBlobUrl} />}
          {modalType === "image" && (
            <img
              src={modalBlobUrl}
              alt={modalTitle}
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          )}
          {modalType === "video" && (
            <video
              src={modalBlobUrl}
              controls
              style={{ width: "100%", height: "100%" }}
            />
          )}
          {modalType === "audio" && (
            <audio
              controls
              src={modalBlobUrl}
              style={{ width: "100%" }}
            />
          )}
          {modalType === "generic" && (
            <iframe
              src={modalBlobUrl}
              title={modalTitle}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MediaCardExtended;
